/**
 * DetailsTabContent - the matter's Overview, and the canvas you land on.
 *
 * IT USED TO BE A READING COLUMN. One measured column, four movements: title,
 * summary, key facts, posture. That fixed the old nine-equal-cards dashboard,
 * but it over-corrected - everything became prose, so nothing had shape. A
 * matter is not an essay; it is a dispute between two parties, with money on
 * it, documents under it and work in front of it.
 *
 * So the Overview is now STRUCTURED, top to bottom:
 *
 *   1. Hero      the parties as the headline - "{Client} v. {Opposing}" —
 *                with the lifecycle chips under it and the edit affordance
 *                beside it.
 *   2. KPI strip four figures the matter is judged on: money, documents,
 *                open work, budget consumed. Each degrades to "—" alone.
 *   3. Two columns
 *                MAIN  summary (prose, with the AI generate action), posture,
 *                      next steps, parties, pricing
 *                SIDE  key facts, a dot timeline, an economics mini card and
 *                      the research sessions linked to the matter
 *
 * Editing did not go away, it is BEHIND AN AFFORDANCE: "Edit details" in the
 * hero (and in the header's ⋯ menu), both of which flip this canvas into the
 * same compact form. Nothing that was editable stopped being editable - see
 * `renderEditForm`. Re-analyze is NOT duplicated here: it already lives in the
 * header's ⋯ menu, wired to the analyze wizard, and one button with two owners
 * is how wiring drifts.
 *
 * Containers earn their glass: the KPI strip, the side cards and the posture
 * block are structured objects; the summary is prose on the canvas, because a
 * paragraph in a box reads as a widget.
 *
 * DATA: everything here comes from props the tab already receives, plus two
 * small reads made only when they can pay for themselves - the economics
 * rollup (only when the economics feature is on) and the matter's research
 * sessions. No figure on this page is invented; a field that is absent renders
 * as "—" or "Not set".
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { useAppDispatch } from '../../../store';
import Badge from '../../shared/TailwindComponents/Badge';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import {
  PiPencilSimpleLight,
  PiSparkleLight,
  PiScalesLight,
  PiTranslateLight,
  PiTagLight,
  PiCalendarBlankLight,
  PiClockCountdownLight,
  PiCurrencyDollarLight,
  PiCheckCircleLight,
  PiCircleDashedLight,
  PiFilesLight,
  PiCheckSquareOffsetLight,
  PiChartPieSliceLight,
  PiArrowRightLight,
  PiListMagnifyingGlassLight,
  PiWalletLight,
} from 'react-icons/pi';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { getMatterStatusColor, getStatusDisplay, MatterData } from '../MatterDetails';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import MatterClients, { MatterClient } from '../MatterClients';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, InfoRow, KpiTile, Well } from '../../shared/brand/Field';
import MatterPricingEditor from '../economics/MatterPricingEditor';
import { isMatterEconomicsEnabled } from '../../../utils/config';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { formatCurrency } from '../../../utils/formatCurrency';
import Flag from '../../shared/TailwindComponents/Flag';
import { getJurisdictionDisplay } from '../../../utils/jurisdictionFlag';
import { languageName } from '../../../utils/languageName';
import { formatDate, formatDateTime, parseServerDate } from '../../../utils/functions';
import {
  fetchMatterDropdownStatuses,
  editMatter,
} from '../../../store/actions/Matters/MatterActions';
import { fetchMatterEconomics } from '../../../store/actions/economicsActions';
import { readBudget, budgetBarWidths } from '../economics/budgetModel';
import { useMatterSurface } from '../surface/MatterSurfaceContext';
import { suggestionId, taskId } from '../tasks/taskConstants';
import { researchListChatGroups, ResearchChatGroup } from '../../../store/actions/ChatAction';
import { normaliseSources, countBySection } from './sources/sourceModel';
import { hasCasesSupport } from '../matterConstants';
import {
  MatterDraft,
  diffMatterDraft,
  draftFromMatter,
  hasTimeComponent,
  parseMatterDate,
  serialiseMatterDate,
  validateMatterDraft,
} from './matterEditForm';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import type { MatterEconomicsSummary } from '../../../types/economics';
import Spinner from '../../shared/TailwindComponents/Spinner';

/* ── Jurisdiction / language display ──────────────────────────────── */

/**
 * Jurisdiction resolution lives in `utils/jurisdictionFlag` - shared with the
 * matter list, the client detail and the dashboard, so all four agree. The
 * 27-country map that used to sit here had no Bulgaria entry, so a Bulgarian
 * matter fell to a fallback that emitted a flag only when the stored value
 * was already two characters long.
 */
function getJurisdictionInfo(raw: string | null | undefined) {
  const display = getJurisdictionDisplay(raw);
  if (!display) return null;
  return { label: display.label, flag: display.countryCode, code: display.code };
}

/** Language codes are not country codes; this map is only for the flag beside one. */
const languageDisplayMap: Record<string, { label: string; flag: string }> = {
  en: { label: 'English', flag: 'GB' },
  sv: { label: 'Swedish', flag: 'SE' },
  de: { label: 'German', flag: 'DE' },
  fr: { label: 'French', flag: 'FR' },
  no: { label: 'Norwegian', flag: 'NO' },
  nb: { label: 'Norwegian', flag: 'NO' },
  da: { label: 'Danish', flag: 'DK' },
  fi: { label: 'Finnish', flag: 'FI' },
  nl: { label: 'Dutch', flag: 'NL' },
  es: { label: 'Spanish', flag: 'ES' },
  it: { label: 'Italian', flag: 'IT' },
  pt: { label: 'Portuguese', flag: 'PT' },
  bg: { label: 'Bulgarian', flag: 'BG' },
  ja: { label: 'Japanese', flag: 'JP' },
  zh: { label: 'Chinese', flag: 'CN' },
  ko: { label: 'Korean', flag: 'KR' },
  ar: { label: 'Arabic', flag: 'SA' },
  ru: { label: 'Russian', flag: 'RU' },
  pl: { label: 'Polish', flag: 'PL' },
  is: { label: 'Icelandic', flag: 'IS' },
  english: { label: 'English', flag: 'GB' },
  swedish: { label: 'Swedish', flag: 'SE' },
  german: { label: 'German', flag: 'DE' },
  french: { label: 'French', flag: 'FR' },
  norwegian: { label: 'Norwegian', flag: 'NO' },
  danish: { label: 'Danish', flag: 'DK' },
  finnish: { label: 'Finnish', flag: 'FI' },
  dutch: { label: 'Dutch', flag: 'NL' },
  spanish: { label: 'Spanish', flag: 'ES' },
  italian: { label: 'Italian', flag: 'IT' },
  portuguese: { label: 'Portuguese', flag: 'PT' },
  bulgarian: { label: 'Bulgarian', flag: 'BG' },
  japanese: { label: 'Japanese', flag: 'JP' },
  chinese: { label: 'Chinese', flag: 'CN' },
  korean: { label: 'Korean', flag: 'KR' },
  arabic: { label: 'Arabic', flag: 'SA' },
  russian: { label: 'Russian', flag: 'RU' },
  polish: { label: 'Polish', flag: 'PL' },
  icelandic: { label: 'Icelandic', flag: 'IS' },
};

function getLanguageInfo(raw: string | null | undefined) {
  if (!raw) return null;
  const key = raw.trim().toLowerCase();
  const mapped = languageDisplayMap[key];
  // The NAME comes from the shared resolver so it cannot drift from the
  // Context pane's; this map now only supplies the flag, which is a country
  // code rather than a language name.
  if (mapped) return { label: languageName(raw) || mapped.label, flag: mapped.flag, code: key };
  // A language value that is really a country code ("SE" for Swedish).
  const asCountry = getJurisdictionDisplay(raw);
  if (asCountry?.countryCode) {
    return { label: asCountry.label, flag: asCountry.countryCode, code: asCountry.code };
  }
  return { label: languageName(raw) || raw, flag: null, code: key };
}

/* ── Parties ──────────────────────────────────────────────────────── */

/**
 * Which side of the "v." a contact sits on.
 *
 * The role taxonomy is org-configurable, so this matches on the SHAPE of the
 * role name rather than on ids: anything that reads as the other side is the
 * other side, everything else is ours. A matter with no opposing party (an
 * advisory instruction, say) simply has no "v." - it is not a missing value.
 */
const OPPOSING_ROLE = /oppos|counter[- ]?part|adverse|respondent|defend|third[- ]?part/i;

function partyName(raw: any): string {
  if (!raw) return '';
  if (typeof raw === 'string') return raw.trim();
  return String(raw.name || raw.ClientName || raw.Name || raw.full_name || '').trim();
}

/** "A", "A & B", "A, B +2" - a headline, not a list. */
function joinParties(names: string[]): string {
  const clean = names.filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} & ${clean[1]}`;
  return `${clean[0]}, ${clean[1]} +${clean.length - 2}`;
}

/** A section heading on the canvas - quiet, and the only rhythm marker. */
const Movement: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-[11px] font-medium text-fg-muted">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

/** One dot on the side timeline. Absent dates stay on the line, muted. */
const TimelineDot: React.FC<{
  label: string;
  value?: string | null;
  tone?: 'default' | 'warning' | 'danger';
  last?: boolean;
}> = ({ label, value, tone = 'default', last }) => {
  const { t } = useTranslation();
  return (
    <li className="relative ps-5 pb-3 last:pb-0">
      {!last && <span className="absolute start-[3px] top-2 bottom-0 w-px bg-hairline" />}
      <span
        className={clsx(
          'absolute start-0 top-1.5 h-[7px] w-[7px] rounded-full',
          !value && 'bg-hairline',
          value && tone === 'default' && 'bg-champagne',
          value && tone === 'warning' && 'bg-warning-500',
          value && tone === 'danger' && 'bg-danger-500'
        )}
      />
      <p className="text-[11px] font-medium text-fg-muted truncate">{label}</p>
      {value ? (
        <p
          title={value}
          className={clsx(
            'text-sm font-medium truncate',
            tone === 'danger'
              ? 'text-danger-600 dark:text-danger-400'
              : tone === 'warning'
              ? 'text-warning-600 dark:text-warning-400'
              : 'text-fg-body'
          )}
        >
          {value}
        </p>
      ) : (
        <p className="text-sm text-fg-muted">{t('Not set')}</p>
      )}
    </li>
  );
};

interface DetailsTabContentProps {
  matterData: MatterData | null;
  userType: string;
  userAccessLevel?: 'edit' | 'admin' | 'view';
  isEditingMatterInfo: boolean;
  setIsEditingMatterInfo: (editing: boolean) => void;
  editedMatterFields: any;
  setEditedMatterFields: (fields: any) => void;
  /**
   * DEPRECATED - the form no longer delegates its write.
   *
   * The parent's save helper rebuilds its own diff from `editedMatterFields`
   * and only knows four of the five editable fields (it cannot see the status),
   * and being a `useCallback` it closes over whatever `editedMatterFields` held
   * on the render that produced it. This form now assembles the complete
   * payload and dispatches `editMatter` itself, exactly once. The prop stays so
   * the parent keeps compiling; nothing here calls it.
   */
  handleSaveMatterInfo?: () => Promise<void>;
  matterStatuses?: Array<{ label: string; value: string }>;
  /**
   * Refresh the matter. The optional patch is what was just saved, applied to
   * the displayed matter straight away so every changed field re-renders
   * without waiting on - or depending on - the refetch.
   */
  onUpdate?: (patch?: Record<string, any>) => void;
  clients?: MatterClient[];
  hasDocuments?: boolean;
  /** The matter's sources payload - read only, for the documents KPI. */
  sources?: any;
  /** Open work, for the posture block. Read-only here - Tasks owns them. */
  matterTasks?: any[];
  suggestedTasks?: any[];
  /** Jump to another tab (the Overview links into Tasks / Economics). */
  onNavigateTab?: (tab: string) => void;
}

const DetailsTabContent: React.FC<DetailsTabContentProps> = ({
  matterData,
  userAccessLevel = 'view',
  isEditingMatterInfo,
  setIsEditingMatterInfo,
  setEditedMatterFields,
  matterStatuses = [],
  onUpdate,
  clients = [],
  hasDocuments = false,
  sources,
  matterTasks = [],
  suggestedTasks = [],
  onNavigateTab,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

  const matterUuid = matterData?.uuid || '';
  /*
    LICENSED PARTS ONLY. The economics card, pricing and budget are the Matter
    economics module; the linked-research card reads `research/chat-groups`.
    On a plan without them they are neither fetched nor drawn - a fetch would
    only 403. Pilot (licensing not enforced) answers yes to both.
  */
  const { hasSectionAccess } = useLicenseGate();
  const economicsOn = isMatterEconomicsEnabled() && hasSectionAccess('matter_economics');
  const researchOn = hasSectionAccess('research');
  const {
    isGenerating: isGeneratingDescription,
    handleGenerateMore: handleGenerateDescription,
    progress: descriptionProgress,
    aiLocked: summaryAiLocked,
  } = useGenerateMore({
    matterUuid,
    targetStep: 'description',
    onComplete: onUpdate ?? (() => {}),
  });

  const [isSaving, setIsSaving] = useState(false);
  /** Belt to the `isSaving` braces: state is async, a second click is not. */
  const savingRef = useRef(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<MatterDraft>(() => draftFromMatter(matterData));
  const [statuses, setStatuses] = useState<Array<{ id?: number; status_id?: number; name: string }>>([]);
  const [statusesLoading, setStatusesLoading] = useState(true);
  const [economics, setEconomics] = useState<MatterEconomicsSummary | null>(null);
  const [research, setResearch] = useState<ResearchChatGroup[]>([]);
  const [researchLoading, setResearchLoading] = useState(false);
  const surface = useMatterSurface();

  /* ── Editing ─────────────────────────────────────────────────────
     ONE draft, held here, written by every control and read by nothing
     else. No field touches the network; Save is the only writer.

     What this replaces: the status dropdown used to call the update
     endpoint from its own `onChange`, which (a) wrote a status the user
     had not confirmed, and (b) triggered the parent's refresh, whose
     success handler re-seeds `editedMatterFields` from the server - so
     picking a status silently discarded every other unsaved edit in the
     form. Local-only state removes both halves of that. */

  /** Re-seed when the form opens, or when we move to a different matter. */
  useEffect(() => {
    if (!isEditingMatterInfo) return;
    setDraft(draftFromMatter(matterData));
    setFormErrors({});
    // Deliberately keyed on the EDIT FLAG and the matter identity, not on
    // `matterData` itself: a background refresh landing mid-edit must not
    // reach in and overwrite what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMatterInfo, matterUuid]);

  /** Every control funnels through here. Local state, nothing else. */
  const setField = useCallback(<K extends keyof MatterDraft>(field: K, value: MatterDraft[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => (prev[field] || prev.general ? { ...prev, [field]: '', general: '' } : prev));
  }, []);

  /** What Save would send. Also what greys the Save button out. */
  const pendingChanges = useMemo(
    () => diffMatterDraft(draft, matterData),
    [draft, matterData]
  );
  const isDirty = Object.keys(pendingChanges).length > 0;

  const exitEditMode = useCallback(() => {
    setIsEditingMatterInfo(false);
    setFormErrors({});
  }, [setIsEditingMatterInfo]);

  const handleSave = async () => {
    // Double-submit guard: the ref settles synchronously, so a second click
    // in the same tick is refused rather than queued behind a state update.
    if (savingRef.current) return;

    const errors = validateMatterDraft(draft, t);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (!matterData?.uuid) return;

    if (!isDirty) {
      exitEditMode();
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    try {
      // ONE request, carrying every edit - including the status, which the
      // parent's save helper never knew about.
      const result = await dispatch(editMatter(matterData.uuid, pendingChanges));
      if (!result) {
        // The thunk swallows the failure and toasts it; say so in the form
        // too, and keep the user's edits on screen.
        setFormErrors({ general: t('Failed to save changes. Please try again.') });
        return;
      }
      /*
        Hand the parent EXACTLY what was saved. It merges the patch into the
        displayed matter before refetching, so the name, type, status, summary
        and due date all update together and at once.

        Bound to a local first: `pendingChanges` is the memo from THIS
        render, and reading it once makes that explicit at the call site.
      */
      const saved = pendingChanges;
      setEditedMatterFields({ ...matterData, ...saved });
      onUpdate?.(saved);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Matter updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
      exitEditMode();
    } catch {
      setFormErrors({ general: t('Failed to save changes. Please try again.') });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /** Discard. Nothing was sent on the way in, so nothing needs undoing. */
  const handleCancel = () => {
    if (savingRef.current) return;
    setDraft(draftFromMatter(matterData));
    setEditedMatterFields(matterData || {});
    exitEditMode();
  };

  /* ── Status ──────────────────────────────────────────────────── */

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setStatusesLoading(true);
        const statusesResult = await dispatch(fetchMatterDropdownStatuses());
        if (Array.isArray(statusesResult)) setStatuses(statusesResult);
      } catch {
        /* same */
      } finally {
        setStatusesLoading(false);
      }
    };
    loadDropdowns();
  }, [dispatch, matterData?.category_id]);

  /** Options for the status control, deduped to what the dropdown needs. */
  const statusOptions = useMemo(
    () => statuses.map((status) => ({
      value: (status.id ?? status.status_id)?.toString() || '',
      label: status.name,
    })),
    [statuses]
  );

  /* ── Economics rollup ────────────────────────────────────────────
     One read, and only when the feature is on. It answers two things the
     Overview is asked for - "how much of the budget is gone" and the mini
     card - so it pays for itself; without it both would sit at "—".

     No refresh hook any more: pricing is written on the Economics tab, and
     this tab unmounts while that one is open, so coming back re-reads. */
  useEffect(() => {
    if (!matterUuid || !economicsOn) return;
    let cancelled = false;
    dispatch(fetchMatterEconomics(matterUuid))
      .then((summary) => { if (!cancelled) setEconomics(summary || null); })
      .catch(() => { if (!cancelled) setEconomics(null); });
    return () => { cancelled = true; };
  }, [dispatch, matterUuid, economicsOn]);

  /* ── Linked research sessions ────────────────────────────────────
     `research/chat-groups` rows carry the matter they were linked to;
     narrowed by uuid because the numeric id is not always in the payload. */
  const matterNumericId = useMemo(() => {
    const id = Number(matterData?.id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [matterData?.id]);

  useEffect(() => {
    if (!matterUuid || !researchOn) return;
    let cancelled = false;
    setResearchLoading(true);
    dispatch(researchListChatGroups(1, 25, undefined, matterNumericId))
      .then((page) => {
        if (cancelled) return;
        setResearch((page?.items || []).filter((s) => s.matter_uuid === matterUuid));
      })
      .catch(() => { if (!cancelled) setResearch([]); })
      .finally(() => { if (!cancelled) setResearchLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, matterUuid, matterNumericId, researchOn]);

  /* ── Derived ─────────────────────────────────────────────────── */

  const jurisdictionRaw = matterData?.Jurisdiction || (matterData as any)?.jurisdiction;
  const languageRaw = (matterData as any)?.language || matterData?.Language;
  const jurisdictionInfo = useMemo(() => getJurisdictionInfo(jurisdictionRaw), [jurisdictionRaw]);
  const languageInfo = useMemo(() => getLanguageInfo(languageRaw), [languageRaw]);
  const dueDateRaw = (matterData as any)?.due_date || (matterData as any)?.DueDate;

  const dueDateInfo = useMemo(() => {
    const due = parseServerDate(dueDateRaw);
    if (!due) return null;
    const diffMs = due.getTime() - Date.now();
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffMs < 0) return { text: t('Overdue'), tone: 'danger' as const };
    if (diffHours <= 48) return { text: t('Due soon'), tone: 'warning' as const };
    if (diffHours / 24 <= 7) return { text: t('This week'), tone: 'warning' as const };
    return null;
  }, [dueDateRaw, t]);

  const createdByName = useMemo(() => {
    const creator = matterData?.collaborators?.find((c: any) => c.role === 'creator');
    return creator?.name || null;
  }, [matterData?.collaborators]);

  /**
   * The hero headline.
   *
   * A matter is a dispute before it is a record, so the parties lead. When
   * there is no opposing side the client alone is the headline; when there is
   * no party at all the matter's own name is, which is what it always was.
   */
  const hero = useMemo(() => {
    const roster: MatterClient[] = clients.length
      ? clients
      : ((matterData?.matter_clients?.clients as any[]) || []) as MatterClient[];

    const ours: string[] = [];
    const theirs: string[] = [];
    roster.forEach((c) => {
      const name = partyName(c);
      if (!name) return;
      (OPPOSING_ROLE.test(c.role_name || '') ? theirs : ours).push(name);
    });
    (matterData?.OpposingParties || []).forEach((p: any) => {
      const name = partyName(p);
      if (name && !theirs.includes(name)) theirs.push(name);
    });

    const matterName = matterData?.MatterName || t('Untitled matter');
    const left = joinParties(ours);
    const right = joinParties(theirs);

    if (left && right) return { title: `${left} v. ${right}`, subtitle: matterName };
    if (left) return { title: left, subtitle: matterName };
    return { title: matterName, subtitle: null as string | null };
  }, [clients, matterData?.matter_clients, matterData?.OpposingParties, matterData?.MatterName, t]);

  /**
   * The next steps.
   *
   * Real open work, not an invented recommendation: tasks already on the
   * matter first, then anything the analysis suggested but nobody accepted.
   */
  const isDone = (task: any) => task.status === 'completed' || task.is_completed;
  const openTaskCount = useMemo(
    () => matterTasks.filter((task) => !isDone(task)).length,
    [matterTasks]
  );
  const openSuggestionCount = useMemo(
    () => suggestedTasks.filter((task) => !isDone(task)).length,
    [suggestedTasks]
  );

  /*
    Next steps carry their IDENTITY, not just a label, because each row is now
    a link. Live tasks resolve through `taskId`, suggestions through
    `suggestionId` - the same resolvers the Tasks tab keys its rows by, so a
    row here selects exactly the row there. A suggestion's index has to be its
    index in the FULL suggestion list, not in this filtered top-five, or the
    fallback `title#index` key would point at a different item.
  */
  const openTasks = useMemo(() => {
    const label = (task: any) => task.title || task.TaskTitle || task.Title || '';
    return [
      ...matterTasks
        .filter((task) => !isDone(task))
        .map((task) => ({ label: label(task), suggested: false, id: taskId(task) })),
      ...suggestedTasks
        .map((task, index) => ({ task, id: suggestionId(task, index) }))
        .filter(({ task }) => !isDone(task))
        .map(({ task, id }) => ({ label: label(task), suggested: true, id })),
    ].filter((task) => task.label).slice(0, 5);
  }, [matterTasks, suggestedTasks]);

  /**
   * Where a next-step row goes.
   *
   * A live task has a page of its own. A suggestion does not - it has no id
   * on the server until it is accepted - so it opens where it lives: the
   * Tasks tab's Suggestions list, selected in the shared detail container.
   * Without an id, or without the surface, there is nowhere to go and the row
   * stays inert rather than linking somewhere that 404s.
   */
  const openNextStep = useCallback((step: { id: string; suggested: boolean }) => {
    if (!step.id) return undefined;
    if (!step.suggested) return () => history.push(`/tasks/${step.id}`);
    if (!surface) return undefined;
    return () => {
      surface.goToTab('Tasks');
      surface.select({ kind: 'suggestion', id: step.id, mode: 'edit' });
    };
  }, [history, surface]);

  /** Documents vs legal authorities - the same split the Documents tab uses. */
  const sourceCounts = useMemo(() => {
    if (!sources) return null;
    try {
      // Same options the Documents tab passes, or this counts a corpus
      // the tab does not render (the 12-vs-2 authorities divergence).
      return countBySection(normaliseSources(sources, { showCases: hasCasesSupport(jurisdictionRaw) }));
    } catch {
      return null;
    }
  }, [sources, jurisdictionRaw]);

  /**
   * The matter's own financial rows.
   *
   * `FinancialData` is a free-form label/value list - it is where a contract
   * value or an amount in dispute actually lives, because the matter model has
   * no dedicated field for either. Rendered as-is rather than guessed at.
   */
  const financialRows = useMemo(
    () => (matterData?.FinancialData || []).filter((row: any) => row?.label && row?.value),
    [matterData?.FinancialData]
  );

  const currency = economics?.currency || matterData?.currency || 'eur';
  /* Pricing is the economics module - not read on a plan without it. */
  const agreedFee = economicsOn ? matterData?.fixed_fee_amount : null;

  /*
    Budget, billed, WIP and the remainder all come from one shared reading, so
    this card, the Economics tab and its donut cannot drift. The matter's own
    `budget_amount` is the fallback only for the first paint after a save,
    before the summary has been refetched.
  */
  const budgetReading = useMemo(
    () => readBudget(economics, matterData?.budget_amount ?? agreedFee ?? null),
    [economics, matterData?.budget_amount, agreedFee]
  );
  const billed = economics ? budgetReading.billed : null;
  const wip = economics ? budgetReading.wip : null;
  // The BUDGET remainder, not `fixed_fee_remaining` - different numbers.
  const remaining = budgetReading.remaining;
  const budgetUsedPct = budgetReading.usedPct;
  const barWidths = useMemo(() => budgetBarWidths(budgetReading), [budgetReading]);

  /**
   * The money KPI: the agreed fee when there is one, otherwise whatever the
   * matter itself recorded as being in dispute. Never both, never invented.
   */
  const moneyKpi = useMemo(() => {
    if (agreedFee != null) {
      return { value: formatCurrency(Number(agreedFee), currency), label: t('Agreed fee') };
    }
    const row = financialRows[0];
    if (row) return { value: String(row.value), label: row.label };
    return { value: '—', label: t('In dispute') };
  }, [agreedFee, currency, financialRows, t]);

  const openResearch = useCallback(
    (uuid?: string) => history.push(uuid ? `/research/${uuid}` : '/research'),
    [history]
  );

  if (!matterData) {
    return (
      <div className="details-canvas details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-6">
        <div className="animate-fadePulse bg-surface-sunken h-8 w-2/3 rounded" />
        <div className="animate-fadePulse bg-surface-sunken h-24 w-full rounded-xl" />
        <div className="animate-fadePulse bg-surface-sunken h-48 w-full rounded-xl" />
      </div>
    );
  }

  /* ── The compact edit form - everything the old card could edit ── */

  /** The due date's granularity is whatever the stored value already had. */
  const dueDateHasTime = hasTimeComponent(dueDateRaw);

  const renderEditForm = () => (
    <div className="mx-auto w-full max-w-3xl">
      <GlassCard
        icon={<PiPencilSimpleLight />}
        title={t('Edit details')}
        subtitle={t('Changes are held here until you save.')}
        actions={
          isDirty ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-champagne">
              <span className="h-1.5 w-1.5 rounded-full bg-champagne" />
              {t('Unsaved changes')}
            </span>
          ) : undefined
        }
      >
        <div className="space-y-5">
          <Input
            type="text"
            id="matter-name"
            label={t('Matter name')}
            placeholder={t('Enter matter name')}
            value={draft.MatterName}
            onChange={(e) => setField('MatterName', e.target.value)}
            error={formErrors.MatterName}
            disabled={isSaving}
            required
            className="w-full"
          />

          <div className="details-grid-2 gap-4">
            <Input
              type="text"
              id="matter-type"
              label={t('Matter type')}
              placeholder={t('e.g. Breach of Contract')}
              value={draft.MatterType}
              onChange={(e) => setField('MatterType', e.target.value)}
              error={formErrors.MatterType}
              disabled={isSaving}
              required
              className="w-full"
            />

            {/* The status is a DRAFT field like any other. It used to write
                straight to the API from this onChange. */}
            <div>
              <label
                htmlFor="matter-status"
                className="block text-sm font-medium text-fg-body mb-1"
              >
                {t('Status')}
              </label>
              {statusesLoading ? (
                <Well className="flex items-center gap-2">
                  <Spinner size="glyph" className="!h-4 !w-4" />
                  <span className="text-sm text-fg-muted">{t('Loading...')}</span>
                </Well>
              ) : statusOptions.length > 0 ? (
                // Dropdown carries no `disabled` prop, so an in-flight save
                // freezes it here rather than by growing that component's API.
                <div className={clsx(isSaving && 'pointer-events-none opacity-60')}>
                  <Dropdown
                    id="matter-status"
                    options={statusOptions}
                    value={draft.status_id}
                    onChange={(value) => setField('status_id', Array.isArray(value) ? value[0] : value)}
                    placeholder={t('Select a status')}
                    className="w-full"
                  />
                </div>
              ) : (
                <Well>
                  <p className="text-sm text-fg-muted">
                    {matterData.status_name || t('No status assigned')}
                  </p>
                </Well>
              )}
            </div>
          </div>

          {/*
            ONE control for date AND time - the same `DateTimePicker` BaseForm
            renders for a `datePicker` field, and the one the task detail uses.
            The native `<input type="date">` it replaces round-tripped through
            `toISOString()`, so a date picked west of Greenwich came back a day
            early; `parseMatterDate` / `serialiseMatterDate` stay in local time.
          */}
          <DateTimePicker
            id="matter-due-date"
            label={t('Due date')}
            selected={parseMatterDate(draft.due_date)}
            onChange={(date) => setField('due_date', serialiseMatterDate(date, dueDateHasTime))}
            placeholder={t('No due date')}
            disabled={isSaving}
            showTimeSelect={dueDateHasTime}
            dateFormat={dueDateHasTime ? 'MMM d, yyyy h:mm aa' : 'MMM d, yyyy'}
            fullWidth
          />

          {/*
            THE SUMMARY IS PROSE, and the only field on this form that is.
            Four rows is right for a field you glance at and wrong for one you
            write a page into, so it carries the expand control: collapsed it
            is the same box as before, expanded it is a writing surface.
          */}
          <Textarea
            id="matter-description"
            label={t('Summary')}
            placeholder={t('Provide a detailed description of the matter')}
            value={draft.Description}
            onChange={(e) => setField('Description', e.target.value)}
            error={formErrors.Description}
            disabled={isSaving}
            rows={6}
            expandable
            className="w-full"
          />

          {formErrors.general && (
            <div className="rounded-lg border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 p-3">
              <p className="text-sm text-danger-700 dark:text-danger-300">{formErrors.general}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-hairline-soft">
            <Button variant="neutral" size="small" onClick={handleCancel} disabled={isSaving}>
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              tooltip={!isDirty && !isSaving ? t('No changes to save') : ''}
              icon={isSaving ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
            >
              {isSaving ? t('Saving...') : t('Save changes')}
            </Button>
          </div>
        </div>
      </GlassCard>
    </div>
  );

  /* ── Render ──────────────────────────────────────────────────── */

  return (
    /*
      `details-canvas` is the container everything below measures itself
      against (see index.css). It has to be the OUTER element - a container
      cannot query its own width - so the max-width and padding live on the
      child, where the query can still reach them.
    */
    <div className="details-canvas w-full">
    <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
      {/* ── 1. Hero ──────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        {/* `flex-1` so the title block takes the slack and its children have
            something finite to truncate against. */}
        <div className="min-w-0 flex-1 space-y-2.5">
          <h1 className="text-3xl font-medium text-fg-strong leading-tight tracking-tight break-words hyphens-auto">
            {hero.title}
          </h1>
          {hero.subtitle && (
            // The parties line ("Acme Holdings, B. Petrov +3") is a summary,
            // not content - one line, clipped, with the full text on hover.
            <p className="text-sm text-fg-muted -mt-1 truncate" title={hero.subtitle}>
              {hero.subtitle}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {matterData.status_name && (
              <Badge
                className="capitalize"
                color={getMatterStatusColor(matterData.status_name, matterStatuses) as BadgeColor}
              >
                {getStatusDisplay(matterData.status_name, t, matterStatuses)}
              </Badge>
            )}
            {matterData.MatterType && (
              <Badge color="gray" className="capitalize">{matterData.MatterType}</Badge>
            )}
            {jurisdictionInfo && (
              <span
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-hairline
                           bg-surface-sunken/70 px-2.5 py-0.5 text-xs text-fg-body"
                title={t(jurisdictionInfo.label)}
              >
                {jurisdictionInfo.flag && (
                  <Flag countryCode={jurisdictionInfo.flag} code={jurisdictionInfo.code} size="sm" />
                )}
                <span className="truncate">{t(jurisdictionInfo.label)}</span>
              </span>
            )}
            {languageInfo && (
              <span
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-hairline
                           bg-surface-sunken/70 px-2.5 py-0.5 text-xs text-fg-body"
                title={t(languageInfo.label)}
              >
                {languageInfo.flag && (
                  <Flag countryCode={languageInfo.flag} code={languageInfo.code} size="sm" />
                )}
                <span className="truncate">{t(languageInfo.label)}</span>
              </span>
            )}
          </div>
        </div>

        {/* Re-analyze deliberately absent: it lives in the header's ⋯ menu,
            already wired to the analyze wizard and its gating. */}
        {canEdit && !isEditingMatterInfo && (
          <Button
            variant="outline"
            size="small"
            className="flex-shrink-0"
            onClick={() => setIsEditingMatterInfo(true)}
            icon={<PiPencilSimpleLight className="h-3.5 w-3.5" />}
          >
            {t('Edit details')}
          </Button>
        )}
      </header>

      {/* ── Edit mode replaces the reading view entirely: a form and the
            prose it edits, side by side, is the clutter this removes. ── */}
      {isEditingMatterInfo && canEdit ? (
        renderEditForm()
      ) : (
        <>
          {/* ── 2. KPI strip ─────────────────────────────────────── */}
          <GlassCard padding="none">
            <div className="details-kpis divide-hairline-soft">
              <KpiTile icon={<PiWalletLight />} label={moneyKpi.label} value={moneyKpi.value} />
              <KpiTile
                icon={<PiFilesLight />}
                label={
                  sourceCounts
                    ? `${t('Documents')} · ${sourceCounts.legal_authority} ${t('authorities')}`
                    : t('Documents')
                }
                value={sourceCounts ? sourceCounts.case_files : '—'}
              />
              <KpiTile
                icon={<PiCheckSquareOffsetLight />}
                label={
                  openSuggestionCount
                    ? `${t('Open tasks')} · ${openSuggestionCount} ${t('suggested')}`
                    : t('Open tasks')
                }
                value={openTaskCount}
              />
              {economicsOn && (
                <KpiTile
                  icon={<PiChartPieSliceLight />}
                  label={
                    budgetUsedPct != null && remaining != null
                      ? `${t('Budget used')} · ${formatCurrency(Number(remaining), currency)} ${t('left')}`
                      : t('Budget used')
                  }
                  value={budgetUsedPct != null ? `${budgetUsedPct}%` : '—'}
                  tone={budgetReading.over ? 'danger' : 'default'}
                />
              )}
            </div>
          </GlassCard>

          {/* ── 3. Two columns ───────────────────────────────────── */}
          <div className="details-body gap-6 items-start">
            {/* ── MAIN ─────────────────────────────────────────── */}
            <div className="min-w-0 space-y-8">
              <Movement
                title={t('Summary')}
                action={
                  canEdit && !isGeneratingDescription && (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleGenerateDescription()}
                      /* Matter AI off the plan: disabled, and the hover says
                         why - Starter keeps its matters, without the AI. */
                      disabled={!hasDocuments || summaryAiLocked}
                      tooltip={
                        summaryAiLocked
                          ? t('AI analysis is not included in your plan. Upgrade to unlock.')
                          : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''
                      }
                      icon={<PiSparkleLight className="h-3.5 w-3.5" />}
                    >
                      {matterData.Description ? t('Regenerate') : t('Generate')}
                    </Button>
                  )
                }
              >
                <div data-tour-id="details-description-card">
                  {isGeneratingDescription
                    || descriptionProgress.status === 'complete'
                    || descriptionProgress.status === 'failed' ? (
                    <FindMoreProgress progress={descriptionProgress} isGenerating={isGeneratingDescription} />
                  ) : matterData.Description ? (
                    // Prose on the canvas, not in a box: a paragraph inside a
                    // card reads as a widget rather than something to read.
                    <p className="text-[15px] leading-relaxed text-fg-body whitespace-pre-wrap break-words">
                      {matterData.Description}
                    </p>
                  ) : (
                    <p className="text-sm text-fg-muted">
                      {t('No summary yet. Generate one from the case files, or write it in Edit details.')}
                    </p>
                  )}
                </div>
              </Movement>

              <Movement title={t('Posture')}>
                <GlassCard tone={dueDateInfo?.tone === 'danger' ? 'danger' : 'default'}>
                  <div className="details-grid-2 gap-4">
                    <Field
                      label={t('Where it stands')}
                      value={
                        matterData.status_name
                          ? getStatusDisplay(matterData.status_name, t, matterStatuses)
                          : undefined
                      }
                    />
                    {/* Stage is not shown anywhere in the matter UI - this
                        Field survived the earlier removal. The data still
                        arrives on `matterData`; only the display is gone. */}
                    <Field
                      label={t('Last analysed')}
                      value={matterData.last_analyzed_at ? formatDateTime(matterData.last_analyzed_at) : undefined}
                      emptyText={t('Not analysed yet')}
                    />
                  </div>
                </GlassCard>
              </Movement>

              <Movement
                title={t('Next steps')}
                action={
                  onNavigateTab && (
                    <Button variant="outline" size="small" onClick={() => onNavigateTab('Tasks')}>
                      {t('All tasks')}
                    </Button>
                  )
                }
              >
                <GlassCard padding="none">
                  {openTasks.length === 0 ? (
                    <p className="px-4 py-5 text-sm text-fg-muted">{t('Nothing outstanding.')}</p>
                  ) : (
                    <ul className="divide-y divide-hairline-soft">
                      {openTasks.map((task, index) => {
                        const open = openNextStep(task);
                        /* A row with nowhere to go stays a plain `div` - an
                           inert button invites a click that can never work. */
                        const Row: any = open ? 'button' : 'div';
                        return (
                          <li key={`${task.id || task.label}-${index}`}>
                            <Row
                              {...(open ? { type: 'button', onClick: open } : {})}
                              className={clsx(
                                'group flex w-full items-start gap-2.5 px-4 py-2.5 text-start',
                                'transition-colors duration-200 ease-slide',
                                open && 'cursor-pointer hover:bg-surface-sunken/60'
                              )}
                            >
                              {task.suggested ? (
                                <PiCircleDashedLight className="h-4 w-4 mt-0.5 text-fg-muted flex-shrink-0" />
                              ) : (
                                <PiCheckCircleLight className="h-4 w-4 mt-0.5 text-champagne flex-shrink-0" />
                              )}
                              <span className="text-sm text-fg-body min-w-0 break-words flex-1">
                                {task.label}
                                {task.suggested && (
                                  <span className="ms-1.5 text-[10px] text-fg-muted">{t('suggested')}</span>
                                )}
                              </span>
                              {open && (
                                <PiArrowRightLight
                                  className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-fg-muted
                                             opacity-0 group-hover:opacity-100 transition-opacity rtl:-scale-x-100"
                                />
                              )}
                            </Row>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </GlassCard>
              </Movement>

              {/* ── Parties and pricing keep their own editors ───── */}
              <Movement title={t('Parties')}>
                <div data-tour-id="details-clients-section">
                  <MatterClients
                    matterUuid={matterData.uuid || ''}
                    clients={clients}
                    canEdit={canEdit}
                    onUpdate={onUpdate}
                  />
                </div>
              </Movement>

              {economicsOn && (
                <Movement title={t('Pricing')}>
                  <MatterPricingEditor
                    matterData={matterData}
                    userAccessLevel={userAccessLevel}
                    currency={currency}
                    /* The rollup's budget, so the card is right even when the
                       matter blob has not caught up with it. */
                    economicsBudget={economics?.budget_amount ?? null}
                    /* Read only for the used/remaining hint on the card. */
                    economics={economics}
                    /* VIEW ONLY here (mat-19): pricing is edited in the
                       Economics tab's side panel, so this card hands focus
                       across rather than opening a form. */
                    onGoToEconomics={onNavigateTab ? () => onNavigateTab('Economics') : undefined}
                  />
                </Movement>
              )}
            </div>

            {/* ── SIDE ─────────────────────────────────────────── */}
            <div className="details-side min-w-0 space-y-4">
              <GlassCard icon={<PiTagLight />} title={t('Key facts')} padding="none">
                <dl className="divide-y divide-hairline-soft">
                  <FactRow icon={<PiTagLight />} label={t('Type')} value={matterData.MatterType} />
                  <FactRow
                    icon={<PiScalesLight />}
                    label={t('Category')}
                    value={matterData.category_name ? t(matterData.category_name) : matterData.category_name}
                  />
                  <FactRow icon={<PiTranslateLight />} label={t('Language')}>
                    {languageInfo && (
                      <span className="inline-flex items-center gap-2">
                        {languageInfo.flag && (
                          <Flag countryCode={languageInfo.flag} code={languageInfo.code} size="sm" />
                        )}
                        {t(languageInfo.label)}
                      </span>
                    )}
                  </FactRow>
                  {economicsOn && agreedFee != null && (
                    <FactRow
                      icon={<PiCurrencyDollarLight />}
                      label={t('Agreed fee')}
                      value={formatCurrency(Number(agreedFee), currency)}
                    />
                  )}
                  {/* The matter model has no contract-value field; this
                      free-form list is where such a figure actually lives. */}
                  {financialRows.map((row: any) => (
                    <FactRow
                      key={row.label}
                      icon={<PiCurrencyDollarLight />}
                      label={row.label}
                      value={row.value}
                    />
                  ))}
                  {matterData.DateClosed && (
                    <FactRow
                      icon={<PiCalendarBlankLight />}
                      label={t('Closed')}
                      value={formatDate(matterData.DateClosed)}
                    />
                  )}
                </dl>
              </GlassCard>

              <GlassCard icon={<PiClockCountdownLight />} title={t('Timeline')}>
                <ol className="mt-0.5">
                  <TimelineDot
                    label={t('Created')}
                    value={matterData.created_at ? formatDate(matterData.created_at) : null}
                  />
                  <TimelineDot
                    label={t('Last analysed')}
                    value={matterData.last_analyzed_at ? formatDateTime(matterData.last_analyzed_at) : null}
                  />
                  <TimelineDot
                    label={t('Due')}
                    value={dueDateRaw ? formatDateTime(dueDateRaw) : null}
                    tone={dueDateInfo?.tone ?? 'default'}
                    last
                  />
                </ol>
                {dueDateInfo && (
                  <Badge color={dueDateInfo.tone === 'danger' ? 'red' : 'yellow'} className="mt-1">
                    {dueDateInfo.text}
                  </Badge>
                )}
              </GlassCard>

              {economicsOn && (
                <GlassCard
                  icon={<PiChartPieSliceLight />}
                  title={t('Economics')}
                  actions={
                    onNavigateTab && (
                      <button
                        type="button"
                        onClick={() => onNavigateTab('Economics')}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-fg-muted
                                   hover:text-fg-body transition-colors duration-200 ease-slide"
                      >
                        {t('Open economics')}
                        <PiArrowRightLight className="h-3 w-3 rtl:-scale-x-100" />
                      </button>
                    )
                  }
                >
                  {!economics ? (
                    <p className="text-sm text-fg-muted">{t('No economics recorded yet.')}</p>
                  ) : (
                    <div className="space-y-3">
                      {budgetReading.hasBudget && (
                        <div>
                          {/* Widths are CLAMPED so an overrun cannot render
                              past the track; the real numbers are the
                              percentage and remainder below. */}
                          <div className="h-1.5 w-full rounded-full bg-surface-sunken overflow-hidden flex">
                            <div
                              className={
                                'h-full transition-all duration-300 ease-slide '
                                + (budgetReading.over ? 'bg-danger-500' : 'bg-champagne')
                              }
                              style={{ width: `${barWidths.billed}%` }}
                            />
                            <div
                              className={
                                'h-full transition-all duration-300 ease-slide '
                                + (budgetReading.over ? 'bg-danger-500/40' : 'bg-champagne/40')
                              }
                              style={{ width: `${barWidths.wip}%` }}
                            />
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-fg-muted">
                            <span className="flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full bg-champagne" />{t('Billed')}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full bg-champagne/40" />{t('WIP')}
                            </span>
                            {budgetUsedPct != null && (
                              <span
                                className={
                                  'ms-auto tabular-nums '
                                  + (budgetReading.over ? 'text-danger-600 dark:text-danger-400' : '')
                                }
                              >
                                {budgetUsedPct}%
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <InfoRow
                          label={t('Budget')}
                          value={budgetReading.budget != null
                            ? formatCurrency(budgetReading.budget, currency)
                            : undefined}
                        />
                        <InfoRow
                          label={t('Billed')}
                          value={billed != null ? formatCurrency(billed, currency) : undefined}
                        />
                        <InfoRow
                          label={t('WIP')}
                          value={wip != null ? formatCurrency(wip, currency) : undefined}
                        />
                        {/* The REAL remainder, negative on overrun - the bar
                            is clamped, this number is not. */}
                        <InfoRow label={t('Remaining')}>
                          {remaining != null ? (
                            <span
                              className={
                                'tabular-nums '
                                + (remaining < 0
                                  ? 'text-danger-600 dark:text-danger-400 font-medium'
                                  : 'text-gray-700 dark:text-gray-200')
                              }
                            >
                              {formatCurrency(remaining, currency)}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </InfoRow>
                      </div>
                    </div>
                  )}
                </GlassCard>
              )}

              {researchOn && (
              <GlassCard
                icon={<PiListMagnifyingGlassLight />}
                title={t('Connected research')}
                actions={
                  <button
                    type="button"
                    onClick={() => openResearch()}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-fg-muted
                               hover:text-fg-body transition-colors duration-200 ease-slide"
                  >
                    {t('Assistant')}
                    <PiArrowRightLight className="h-3 w-3 rtl:-scale-x-100" />
                  </button>
                }
                padding={research.length ? 'none' : 'md'}
              >
                {researchLoading ? (
                  <p className="text-sm text-fg-muted">{t('Loading...')}</p>
                ) : research.length === 0 ? (
                  <p className="text-sm text-fg-muted">
                    {t('No research sessions are linked to this matter yet.')}
                  </p>
                ) : (
                  <ul className="divide-y divide-hairline-soft">
                    {research.slice(0, 5).map((session) => (
                      <li key={session.uuid}>
                        <button
                          type="button"
                          onClick={() => openResearch(session.uuid)}
                          className="group w-full text-start px-4 py-2.5 transition-colors duration-200
                                     ease-slide hover:bg-surface-sunken/60"
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span
                              className="min-w-0 flex-1 truncate text-sm text-fg-body"
                              title={session.title || t('Untitled session')}
                            >
                              {session.title || t('Untitled session')}
                            </span>
                            <PiArrowRightLight
                              className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted opacity-0
                                         group-hover:opacity-100 transition-opacity rtl:-scale-x-100"
                            />
                          </span>
                          {session.last_message_at && (
                            <span className="block truncate text-[10px] text-fg-muted">
                              {formatDateTime(session.last_message_at)}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </GlassCard>
              )}

              {/* ── Provenance, deliberately last and deliberately quiet ── */}
              {(createdByName || matterData.created_at || matterData.updated_at) && (
                <div
                  data-tour-id="details-status-section"
                  className="pt-1 space-y-1.5"
                >
                  {createdByName && <InfoRow label={t('Created by')} value={createdByName} />}
                  {matterData.updated_at && (
                    <InfoRow label={t('Last updated')} value={formatDateTime(matterData.updated_at)} />
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
    </div>
  );
};

/**
 * One aligned label ⟷ value row inside the key-facts block.
 *
 * A definition list rather than tiles: nine facts as nine bordered boxes was
 * the grid that made this page read as a dashboard. Rows share one left edge,
 * so the eye runs down the values.
 */
const FactRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ icon, label, value, children }) => {
  const { t } = useTranslation();
  const content = children ?? value;
  return (
    <div className="details-fact-row px-4 py-2.5">
      <dt className="flex items-center gap-2 text-xs text-fg-muted min-w-0">
        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 flex-shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </dt>
      {/* `break-words` not `truncate`: a fact value is the thing the row
          exists to show, and a long category name is better on two lines
          than cut off. The grid track is `minmax(0, …)` so it can shrink. */}
      <dd className="text-sm font-medium text-fg-body min-w-0 break-words">
        {content || content === 0 ? content : <span className="text-fg-muted font-normal">{t('Not set')}</span>}
      </dd>
    </div>
  );
};

export default DetailsTabContent;
